import React, { useState, useMemo, useCallback } from 'react';
import { BarChart3, Target, FolderOpen, Bot, TrendingUp, Calendar, FlaskConical, FileText, CheckCircle, XCircle, AlertTriangle, Lightbulb, BookOpen } from 'lucide-react';
import { TestResult, TestRun, SourceAnalysis, ActionItem, RunComparison } from '../types';
import QuestionComparison from './QuestionComparison';


import './TestResults.css';

interface TestResultsProps {
  testRuns?: TestRun[];
  currentRun?: TestRun;
  onLoadTestRun?: (filename: string) => Promise<TestRun>;
  onAnalyzeResults?: (prompt: string, results: TestResult[]) => Promise<string>;
}

type FilterMode = 'all' | 'satisfactory' | 'unsatisfactory' | 'covered';
type SortBy = 'question' | 'source_status';

const TestResults: React.FC<TestResultsProps> = ({
  testRuns = [],
  currentRun,
  onLoadTestRun,
  onAnalyzeResults
}) => {




  // State declarations
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [sortBy, setSortBy] = useState<SortBy>('question');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  // Set default tab
  const getDefaultTab = (): 'results' | 'actions' | 'sources' | 'analysis' | 'comparison' => 'results';
  const [activeTab, setActiveTab] = useState<'results' | 'actions' | 'sources' | 'analysis' | 'comparison'>(getDefaultTab());
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [analysisResult, setAnalysisResult] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedSourceFilters, setSelectedSourceFilters] = useState<Set<string>>(new Set());
  const [selectedSourceStatusFilters, setSelectedSourceStatusFilters] = useState<Set<string>>(new Set());

  // Log when TestResults receives new data
  React.useEffect(() => {
    if (currentRun && currentRun.results.length > 0) {
       }
  }, [currentRun]);





  // Extract source filenames from sources string in format [n] filename, Page info
  const extractSourceFilenames = useCallback((sources: string): string[] => {
    if (!sources) return [];
    
    // Split by lines first to handle newline-separated sources
    const lines = sources.split('\n');
    const matches: string[] = [];
    
    lines.forEach(line => {
      // Match pattern: [n] filename, Page n
      const match = line.match(/\[(\d+)\]\s*([^,]+),\s*Page\s*\d+/);
      if (match) {
        const filename = match[2].trim();
        if (filename && !matches.includes(filename)) {
          matches.push(filename);
        }
      }
    });
    
    return matches;
  }, []);

  // Filter and sort results
  const filteredResults = useMemo(() => {
    if (!currentRun) return [];

    let filtered = currentRun.results.filter(result => {
      // Apply filter mode
      switch (filterMode) {
        case 'satisfactory':
          return result.is_satisfactory;
        case 'unsatisfactory':
          return !result.is_satisfactory;
        case 'covered':
          return result.is_satisfactory;
        default:
          return true;
      }
    });

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(result =>
        result.question.toLowerCase().includes(query) ||
        result.generated_answer.toLowerCase().includes(query) ||
        result.analysis.toLowerCase().includes(query)
      );
    }

    // Apply source filters
    if (selectedSourceFilters.size > 0) {
      filtered = filtered.filter(result => {
        const sourceFilenames = extractSourceFilenames(result.sources);
        return sourceFilenames.some(filename => selectedSourceFilters.has(filename));
      });
    }

    // Apply source status filters
    if (selectedSourceStatusFilters.size > 0) {
      filtered = filtered.filter(result => {
        return selectedSourceStatusFilters.has(result.source_status);
      });
    }

    // Sort results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'source_status':
          return a.source_status.localeCompare(b.source_status);
        default:
          return a.question.localeCompare(b.question);
      }
    });

    return filtered;
  }, [currentRun, filterMode, searchQuery, selectedSourceFilters, selectedSourceStatusFilters, sortBy, extractSourceFilenames]);



  // Generate action items based on source_status
  const actionItems = useMemo((): ActionItem[] => {
    if (!currentRun) return [];

    const items: Map<string, ActionItem> = new Map();

    currentRun.results.forEach(result => {
      if (result.is_satisfactory) return; // Skip satisfactory results

      const key = result.source_status;
      let category: ActionItem['category'];
      let description: string;
      let priority: ActionItem['priority'] = 'medium';

      switch (result.source_status) {
        case 'sufficient_context_retrieved':
          category = 'prompt_improvement';
          description = 'Improve agent prompts and reasoning - sources contain sufficient information but response quality is poor';
          priority = 'high';
          break;
        case 'sufficient_context_found':
          category = 'knowledge_structure';
          description = 'Improve knowledge base structure, search algorithms, or query processing';
          priority = 'medium';
          break;
        case 'insufficient_knowledge_base':
          category = 'knowledge_expansion';
          description = 'Expand knowledge base with additional relevant content';
          priority = 'high';
          break;
        case 'partial_context_available':
          category = 'knowledge_expansion';
          description = 'Enhance existing knowledge base content with more comprehensive information';
          priority = 'medium';
          break;
        default:
          category = 'knowledge_expansion';
          description = 'Review and address knowledge gaps';
      }

      if (items.has(key)) {
        const existingItem = items.get(key)!;
        existingItem.affected_questions.push(result.question);
        existingItem.frequency++;
      } else {
        items.set(key, {
          category,
          priority,
          description,
          affected_questions: [result.question],
          source_status: result.source_status,
          frequency: 1
        });
      }
    });

    return Array.from(items.values()).sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority] || b.frequency - a.frequency;
    });
  }, [currentRun]);

  // Generate source analysis
  const sourceAnalysis = useMemo((): SourceAnalysis[] => {
    if (!currentRun) return [];

    const sourceMap: Map<string, SourceAnalysis> = new Map();

    currentRun.results.forEach(result => {
      const filenames = extractSourceFilenames(result.sources);
      
      filenames.forEach(filename => {
        const baseFilename = filename; // No need to process filename further
        
        if (!sourceMap.has(baseFilename)) {
          sourceMap.set(baseFilename, {
            filename: filename,
            base_filename: baseFilename,
            satisfactory_appearances: 0,
            unsatisfactory_appearances: 0,
            total_appearances: 0,
            success_rate: 0,
            contexts: {
              sufficient_context_retrieved: 0,
              sufficient_context_found: 0,
              insufficient_knowledge_base: 0,
              partial_context_available: 0
            }
          });
        }

        const analysis = sourceMap.get(baseFilename)!;
        analysis.total_appearances++;
        
        if (result.is_satisfactory) {
          analysis.satisfactory_appearances++;
        } else {
          analysis.unsatisfactory_appearances++;
        }

        // Only increment context count if source_status is a known context type
        if (result.source_status in analysis.contexts) {
          analysis.contexts[result.source_status as keyof typeof analysis.contexts]++;
        }
        analysis.success_rate = analysis.satisfactory_appearances / analysis.total_appearances;
      });
    });

    return Array.from(sourceMap.values()).sort((a, b) => b.total_appearances - a.total_appearances);
  }, [currentRun, extractSourceFilenames]);

  // Get unique source filenames for filtering
  const availableSourceFilenames = useMemo(() => {
    if (!currentRun) return [];
    const filenames = new Set<string>();
    currentRun.results.forEach(result => {
      extractSourceFilenames(result.sources).forEach(filename => {
        filenames.add(filename);
      });
    });
    return Array.from(filenames).sort();
  }, [currentRun, extractSourceFilenames]);

  const handleAnalyze = async () => {
    if (!onAnalyzeResults || !analysisPrompt.trim() || !currentRun) return;

    setIsAnalyzing(true);
    try {
      const result = await onAnalyzeResults(analysisPrompt, filteredResults);
      setAnalysisResult(result);
    } catch (error) {
      console.error('Analysis failed:', error);
      setAnalysisResult('Analysis failed. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleCardExpansion = (index: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCards(newExpanded);
  };

  const toggleSourceFilter = (filename: string) => {
    const newFilters = new Set(selectedSourceFilters);
    if (newFilters.has(filename)) {
      newFilters.delete(filename);
    } else {
      newFilters.add(filename);
    }
    setSelectedSourceFilters(newFilters);
  };

  const toggleSourceStatusFilter = (status: string) => {
    const newFilters = new Set(selectedSourceStatusFilters);
    if (newFilters.has(status)) {
      newFilters.delete(status);
    } else {
      newFilters.add(status);
    }
    setSelectedSourceStatusFilters(newFilters);
  };

  // Get available source status values
  const availableSourceStatuses = useMemo(() => {
    if (!currentRun) return [];
    const statuses = new Set<string>();
    currentRun.results.forEach(result => {
      if (result.source_status) {
        statuses.add(result.source_status);
      }
    });
    return Array.from(statuses).sort();
  }, [currentRun]);

  // Get user-friendly labels for source status values
  const getSourceStatusLabel = (status: string): string => {
    switch (status) {
      case 'sufficient_context_retrieved':
        return 'Sufficient Context Retrieved';
      case 'sufficient_context_found':
        return 'Sufficient Context Found';
      case 'insufficient_knowledge_base':
        return 'Insufficient Knowledge Base';
      case 'partial_context_available':
        return 'Partial Context Available';
      default:
        return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  // Get detailed tooltips for source status values
  const getSourceStatusTooltip = (status: string): string => {
    switch (status) {
      case 'sufficient_context_retrieved':
        return 'The query generated by the bot found information that was sufficient for answering the question. This indicates successful knowledge retrieval on the first attempt.';
      case 'sufficient_context_found':
        return 'The bot didn\'t find enough information initially, but when looking at the ideal answer and asking multiple queries, it was able to find sufficient information. This suggests the initial query could be improved.';
      default:
        return `Status: ${status.replace(/_/g, ' ')}`;
    }
  };

  // Markdown rendering utility for generated answers
  const renderMarkdownContent = (content: string) => {
    if (!content) return null;

    // Split content into paragraphs and process markdown-like formatting
    const paragraphs = content.split('\n\n').filter(p => p.trim());
    
    return (
      <div className="markdown-content">
        {paragraphs.map((paragraph, index) => {
          const trimmedParagraph = paragraph.trim();
          
          // Handle headers (### format)
          if (trimmedParagraph.startsWith('###')) {
            return (
              <h4 key={index} className="markdown-header">
                {trimmedParagraph.replace(/^###\s*/, '')}
              </h4>
            );
          }
          
          // Handle numbered lists
          if (trimmedParagraph.includes('\n') && /^\d+\./.test(trimmedParagraph)) {
            const listItems = trimmedParagraph.split('\n').filter(item => item.trim());
            return (
              <ol key={index} className="markdown-list">
                {listItems.map((item, itemIndex) => {
                  const cleanItem = item.replace(/^\d+\.\s*/, '').replace(/^\*\*(.+?)\*\*:?\s*/, '<strong>$1:</strong> ');
                  return (
                    <li key={itemIndex} 
                        className="markdown-list-item"
                        dangerouslySetInnerHTML={{ __html: cleanItem }} 
                    />
                  );
                })}
              </ol>
            );
          }
          
          // Handle bullet lists (- format)
          if (trimmedParagraph.includes('\n') && trimmedParagraph.includes('- ')) {
            const listItems = trimmedParagraph.split('\n').filter(item => item.trim() && item.includes('- '));
            return (
              <ul key={index} className="markdown-list">
                {listItems.map((item, itemIndex) => {
                  const cleanItem = item.replace(/^-\s*/, '').replace(/^\*\*(.+?)\*\*:?\s*/, '<strong>$1:</strong> ');
                  return (
                    <li key={itemIndex} 
                        className="markdown-list-item"
                        dangerouslySetInnerHTML={{ __html: cleanItem }} 
                    />
                  );
                })}
              </ul>
            );
          }
          
          // Handle regular paragraphs with inline formatting
          let formattedParagraph = trimmedParagraph
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') // Bold text
            .replace(/\[(\d+)\]/g, '<span class="citation-ref">[$1]</span>') // Citations
            .replace(/(\d+)\.\s*\*\*(.+?)\*\*:?\s*/g, '<div class="numbered-item"><strong>$1. $2:</strong> ') // Numbered items with bold
            .replace(/\n/g, '<br />'); // Line breaks
          
          return (
            <p key={index} 
               className="markdown-paragraph"
               dangerouslySetInnerHTML={{ __html: formattedParagraph }} 
            />
          );
        })}
      </div>
    );
  };

  const getScoreColor = (score: number): string => {
    if (score >= 4) return '#22c55e';
    if (score >= 3) return '#f59e0b';
    return '#ef4444';
  };

  const getSourceStatusColor = (status: string): string => {
    switch (status) {
      case 'sufficient_context_retrieved': return '#22c55e';
      case 'sufficient_context_found': return '#3b82f6';
      case 'insufficient_knowledge_base': return '#ef4444';
      case 'partial_context_available': return '#f59e0b';
      default: return '#6f747b';
    }
  };

  const renderFiltersAndControls = () => (
    <div className="test-results-controls">
      <div className="controls-row">
        <div className="filter-group">
          <label>Filter:</label>
          <select value={filterMode} onChange={(e) => setFilterMode(e.target.value as FilterMode)}>
            <option value="all">All Results</option>
            <option value="satisfactory">Satisfactory Only</option>
            <option value="unsatisfactory">Unsatisfactory Only</option>
            <option value="covered">Covered (Satisfactory)</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)}>
            <option value="question">Question</option>
            <option value="source_status">Source Status</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search questions, answers, or analysis..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {availableSourceFilenames.length > 0 && (
        <div className="controls-row">
          <div className="filter-group">
            <label>Source Documents:</label>
            <div className="source-filters">
              {availableSourceFilenames.map(filename => (
                <button
                  key={filename}
                  className={`source-filter-btn ${selectedSourceFilters.has(filename) ? 'active' : ''}`}
                  onClick={() => toggleSourceFilter(filename)}
                >
                  {filename}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {availableSourceStatuses.length > 0 && (
        <div className="controls-row">
          <div className="filter-group">
            <label>Source Status:</label>
            <div className="source-status-filters">
              {availableSourceStatuses.map(status => (
                <button
                  key={status}
                  className={`source-status-filter-btn ${selectedSourceStatusFilters.has(status) ? 'active' : ''}`}
                  onClick={() => toggleSourceStatusFilter(status)}
                  title={getSourceStatusTooltip(status)}
                >
                  {getSourceStatusLabel(status)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="results-summary">
        <span>Showing {filteredResults.length} of {currentRun?.results.length || 0} results</span>
        {currentRun && (
          <span>
            • {currentRun.satisfactory_count} satisfactory ({Math.round((currentRun.satisfactory_count / currentRun.total_questions) * 100)}%)
            • {currentRun.unsatisfactory_count} unsatisfactory ({Math.round((currentRun.unsatisfactory_count / currentRun.total_questions) * 100)}%)
          </span>
        )}
      </div>
    </div>
  );



  const renderCardView = () => (
    <div className="test-results-cards">
      {filteredResults.map((result, index) => (
        <div key={index} className={`result-card ${result.is_satisfactory ? 'satisfactory' : 'unsatisfactory'}`}>
          <div className="card-header">
            <div className="card-status">
              <span className={`satisfaction-badge ${result.is_satisfactory ? 'satisfactory' : 'unsatisfactory'}`}>
                {result.is_satisfactory ? (
                  <>
                    <CheckCircle className="h-3 w-3 inline mr-1" />
                    Satisfactory
                  </>
                ) : (
                  <>
                    <XCircle className="h-3 w-3 inline mr-1" />
                    Unsatisfactory
                  </>
                )}
              </span>
              <span 
                className="source-status-badge"
                style={{ backgroundColor: getSourceStatusColor(result.source_status) }}
                title={getSourceStatusTooltip(result.source_status)}
              >
                {result.source_status.replace(/_/g, ' ')}
              </span>
            </div>
            <button 
              className="expand-btn"
              onClick={() => toggleCardExpansion(index)}
            >
              {expandedCards.has(index) ? '📕' : '📖'}
            </button>
          </div>
          
          <div className="card-question">
            <h4>Question:</h4>
            <p>{result.question}</p>
          </div>
          
          <div className="card-scores">
            <div className="score-item">
              <span>Completeness:</span>
              <span className="score" style={{ color: getScoreColor(result.completeness) }}>
                {result.completeness}/5
              </span>
            </div>
            <div className="score-item">
              <span>Correctness:</span>
              <span className="score" style={{ color: getScoreColor(result.correctness) }}>
                {result.correctness}/5
              </span>
            </div>
            <div className="score-item">
              <span>Conciseness:</span>
              <span className="score" style={{ color: getScoreColor(result.conciseness) }}>
                {result.conciseness}/5
              </span>
            </div>
          </div>
          
          {expandedCards.has(index) && (
            <div className="card-expanded">
              <div className="card-section">
                <h5>Generated Answer:</h5>
                {renderMarkdownContent(result.generated_answer)}
              </div>
              
              <div className="card-section">
                <h5>Ideal Answer:</h5>
                <p>{result.ideal_answer}</p>
              </div>
              
              <div className="card-section">
                <h5>Analysis:</h5>
                <p>{result.analysis}</p>
              </div>
              
              <div className="card-section">
                <h5>Source Analysis:</h5>
                <p>{result.source_analysis}</p>
              </div>
              
              <div className="card-section">
                <h5>Query Used:</h5>
                <p>{result.query}</p>
              </div>
              
              {result.source_queries_used && (
                <div className="card-section">
                  <h5>Source Queries Tried:</h5>
                  <div className="source-queries">
                    {result.source_queries_used.includes('|') ? (
                      <ul className="query-list">
                        {result.source_queries_used.split('|').map((query, qIndex) => (
                          <li key={qIndex} className="query-item">{query.trim()}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="single-query">{result.source_queries_used}</p>
                    )}
                  </div>
                </div>
              )}
              
              {result.source_content && (
                <div className="card-section source-content-section">
                  <details className="source-content-details">
                    <summary className="source-content-summary">
                      <h5>📄 Source Content <span className="expand-hint">(click to expand)</span></h5>
                    </summary>
                    <div className="source-content-body">
                      {renderMarkdownContent(result.source_content)}
                    </div>
                  </details>
                </div>
              )}
              
              {result.incident && (
                <div className="card-section">
                  <h5>Incident ID:</h5>
                  <p className="incident-id">{result.incident}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderActionItems = () => (
    <div className="action-items">
      <h3>Action Items</h3>
      <p className="section-description">
        Categorized improvement recommendations based on source status analysis from unsatisfactory results (where <code>is_satisfactory = false</code>).
      </p>
      
      <div className="csv-mapping-info">
        <details>
          <summary className="csv-mapping-summary">
            <Lightbulb className="h-4 w-4 inline mr-1" />
            How categories are determined from CSV data
          </summary>
          <div className="csv-mapping-content">
            <div className="mapping-rule">
              <strong>
                <Target className="h-3 w-3 inline mr-1" />
                Prompt Improvement:
              </strong> When <code>source_status = "sufficient_context_retrieved"</code>
              <br />
              <span className="rule-explanation">Sources contain good information, but the agent's reasoning/prompts need work</span>
            </div>
            <div className="mapping-rule">
              <strong>
                <FolderOpen className="h-3 w-3 inline mr-1" />
                Knowledge Structure:
              </strong> When <code>source_status = "sufficient_context_found"</code>
              <br />
              <span className="rule-explanation">Information exists but search/query processing needs improvement</span>
            </div>
            <div className="mapping-rule">
              <strong>
                <BookOpen className="h-3 w-3 inline mr-1" />
                Knowledge Expansion:
              </strong> When <code>source_status = "insufficient_knowledge_base"</code> or <code>"partial_context_available"</code>
              <br />
              <span className="rule-explanation">Knowledge base needs more content or more comprehensive information</span>
            </div>
          </div>
        </details>
      </div>
      
      {actionItems.map((item, index) => (
        <div key={index} className={`action-item ${item.category} ${item.priority}`}>
          <div className="action-header">
            <span className={`category-badge ${item.category}`}>
              {item.category.replace(/_/g, ' ')}
            </span>
            <span className={`priority-badge ${item.priority}`}>
              {item.priority} priority
            </span>
            <span className="frequency-badge">
              {item.frequency} questions affected
            </span>
          </div>
          
          <div className="action-description">
            <p>{item.description}</p>
            <div className="csv-trigger">
              <code>source_status = "{item.source_status}"</code> + <code>is_satisfactory = false</code>
            </div>
          </div>
          
          <div className="affected-questions">
            <details>
              <summary>Affected Questions ({item.affected_questions.length})</summary>
              <ul>
                {item.affected_questions.map((question, qIndex) => (
                  <li key={qIndex}>{question}</li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSourceAnalysis = () => (
    <div className="source-analysis">
      <h3>Source File Analysis</h3>
      <p className="section-description">
        Frequency and success rate analysis of source files in test results.
      </p>
      
      <div className="source-analysis-table">
        <div className="table-header">
          <div className="table-cell header">Source File</div>
          <div className="table-cell header">Total Uses</div>
          <div className="table-cell header">Success Rate</div>
          <div className="table-cell header">Context Distribution</div>
        </div>
        
        {sourceAnalysis.map((source, index) => (
          <div key={index} className="table-row">
            <div className="table-cell">
              <strong>{source.base_filename}</strong>
            </div>
            
            <div className="table-cell">
              {source.total_appearances}
              <small>({source.satisfactory_appearances} success, {source.unsatisfactory_appearances} failure)</small>
            </div>
            
            <div className="table-cell">
              <div className="success-rate">
                <div 
                  className="success-bar"
                  style={{ 
                    width: `${source.success_rate * 100}%`,
                    backgroundColor: source.success_rate > 0.7 ? '#22c55e' : source.success_rate > 0.4 ? '#f59e0b' : '#ef4444'
                  }}
                ></div>
                <span>{Math.round(source.success_rate * 100)}%</span>
              </div>
            </div>
            
            <div className="table-cell">
              <div className="context-breakdown">
                {Object.entries(source.contexts).map(([context, count]) => (
                  count > 0 && (
                    <span 
                      key={context}
                      className="context-badge"
                      style={{ backgroundColor: getSourceStatusColor(context) }}
                    >
                      {context.replace(/_/g, ' ')}: {count}
                    </span>
                  )
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalysisTab = () => (
    <div className="ai-analysis">
      <h3>AI Analysis Assistant</h3>
      <p className="section-description">
        Use AI to analyze test results and provide insights. Enter a prompt to get analysis of the current filtered results.
      </p>
      
      <div className="analysis-controls">
        <div className="prompt-section">
          <label htmlFor="analysis-prompt">Analysis Prompt:</label>
          <textarea
            id="analysis-prompt"
            value={analysisPrompt}
            onChange={(e) => setAnalysisPrompt(e.target.value)}
            placeholder="E.g., 'Analyze the common patterns in unsatisfactory results and suggest specific improvements to our knowledge base structure.'"
            rows={3}
          />
        </div>
        
        <div className="analysis-actions">
          <button 
            onClick={handleAnalyze}
            disabled={!analysisPrompt.trim() || isAnalyzing || !currentRun}
            className="analyze-btn"
          >
            {isAnalyzing ? '🤔 Analyzing...' : '🔍 Analyze Results'}
          </button>
          
          <div className="preset-prompts">
            <h5>Quick Prompts:</h5>
            <button onClick={() => setAnalysisPrompt("Identify the most common failure patterns and prioritize improvement areas.")}>
              Failure Patterns
            </button>
            <button onClick={() => setAnalysisPrompt("Analyze source file performance and recommend knowledge base optimizations.")}>
              Source Performance
            </button>
            <button onClick={() => setAnalysisPrompt("Compare scores across different question types and suggest targeted improvements.")}>
              Score Analysis
            </button>
          </div>
        </div>
      </div>
      
      {analysisResult && (
        <div className="analysis-result">
          <h4>Analysis Result:</h4>
          <div className="result-content">
            {analysisResult.split('\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!currentRun) {
    return (
      <div className="test-results-empty">
        <h3>
          <FlaskConical className="h-5 w-5 inline mr-2" />
          Test Results Viewer
        </h3>
        <p>No test run loaded. Please select a test run to view results.</p>
        {testRuns.length > 0 && (
          <div className="available-runs">
            <h4>Available Test Runs:</h4>
            <ul>
              {testRuns.map(run => (
                <li key={run.id}>
                  <button onClick={() => onLoadTestRun?.(run.filename)}>
                    {run.filename} - {run.timestamp} ({run.total_questions} questions)
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="test-results-container">
      <div className="test-results-header">
        <h3>
          <FlaskConical className="h-5 w-5 inline mr-2" />
          Test Results: {currentRun.filename}
        </h3>
        <div className="run-info">
          <span>
            <Calendar className="h-4 w-4 inline mr-1" />
            {currentRun.timestamp}
          </span>
          <span>
            <Target className="h-4 w-4 inline mr-1" />
            {currentRun.endpoint}
          </span>
          <span>
            <BarChart3 className="h-4 w-4 inline mr-1" />
            {currentRun.total_questions} questions
          </span>
          <span className="success-rate">
            <CheckCircle className="h-4 w-4 inline mr-1" />
            {Math.round((currentRun.satisfactory_count / currentRun.total_questions) * 100)}% success
          </span>
          {currentRun.id?.startsWith('real-') && (
            <span className="data-source real-data">
              <FileText className="h-4 w-4 inline mr-1" />
              {currentRun.filename} ({currentRun.total_questions} entries)
            </span>
          )}
          {currentRun.id?.startsWith('fallback') && (
            <span className="data-source fallback-data">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              Fallback Data
            </span>
          )}
        </div>
      </div>

      <nav className="test-results-tabs">
        {[
          { id: 'results' as const, label: 'Test Results', Icon: BarChart3 },
          { id: 'actions' as const, label: 'Action Items', Icon: Target },
          { id: 'sources' as const, label: 'Source Analysis', Icon: FolderOpen },
          { id: 'analysis' as const, label: 'AI Analysis', Icon: Bot },
          { id: 'comparison' as const, label: 'Question Trends', Icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.Icon className="h-4 w-4 inline mr-1" />
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="test-results-content">
        
        {activeTab === 'results' && (
          <>
            {renderFiltersAndControls()}
            {renderCardView()}
          </>
        )}
        
        {activeTab === 'actions' && renderActionItems()}
        {activeTab === 'sources' && renderSourceAnalysis()}
        {activeTab === 'analysis' && renderAnalysisTab()}
        {activeTab === 'comparison' && (
          <QuestionComparison 
            testRuns={testRuns}
            onCompare={(comparison: RunComparison) => {
              console.log('Comparison result:', comparison);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TestResults;